import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Progress,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, RepeatIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../services/apiClient';

const QUOTA_TYPES = ['locations', 'uploadedcontent', 'playlists', 'scenarios', 'users', 'aigenerations'] as const;
type QuotaType = (typeof QUOTA_TYPES)[number];

interface QuotaInfo {
  quotaType: QuotaType;
  planDefault: number;
  limitOverride: number | null;
  effectiveLimit: number;
  currentUsage: number;
  overrideReason?: string | null;
}

interface QuotaOverrideRequest {
  limit: number;
  reason: string;
}

const QUOTA_TYPE_I18N: Record<QuotaType, string> = {
  locations: 'admin.quotas.locations',
  uploadedcontent: 'admin.quotas.uploadedContent',
  playlists: 'admin.quotas.playlists',
  scenarios: 'admin.quotas.scenarios',
  users: 'admin.quotas.users',
  aigenerations: 'admin.quotas.aiGenerations',
};

export function QuotaTab({ organizationId }: { organizationId: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const textColor = useColorModeValue('navy.700', 'white');
  const secondaryTextColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const [editingType, setEditingType] = useState<QuotaType | null>(null);
  const [editLimit, setEditLimit] = useState('');
  const [editReason, setEditReason] = useState('');

  const queryKey = ['admin-org-quotas', organizationId];

  const { data: quotas, isLoading } = useQuery({
    queryKey,
    queryFn: () => apiClient.get<QuotaInfo[]>(`/admin/organizations/${organizationId}/quotas`),
  });

  const saveMutation = useMutation({
    mutationFn: ({ type, body }: { type: QuotaType; body: QuotaOverrideRequest }) =>
      apiClient.put(`/admin/organizations/${organizationId}/quotas/${type}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setEditingType(null);
      toast({ title: t('admin.quotas.overrideSaved'), status: 'success', duration: 3000 });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (type: QuotaType) =>
      apiClient.delete(`/admin/organizations/${organizationId}/quotas/${type}/override`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: t('admin.quotas.overrideRemoved'), status: 'info', duration: 3000 });
    },
  });

  const reconcileMutation = useMutation({
    mutationFn: () =>
      apiClient.post<QuotaInfo[]>(`/admin/organizations/${organizationId}/quotas/reconcile`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: t('admin.quotas.reconcileSuccess'), status: 'success', duration: 3000 });
    },
  });

  function startEdit(quota: QuotaInfo) {
    setEditingType(quota.quotaType);
    setEditLimit(String(quota.limitOverride ?? quota.planDefault));
    setEditReason(quota.overrideReason ?? '');
  }

  function cancelEdit() {
    setEditingType(null);
    setEditLimit('');
    setEditReason('');
  }

  function handleSave(type: QuotaType) {
    const limit = parseInt(editLimit, 10);
    if (isNaN(limit) || limit < 0) return;
    saveMutation.mutate({ type, body: { limit, reason: editReason } });
  }

  if (isLoading) return <Spinner size="sm" />;

  const displayQuotas = quotas ?? QUOTA_TYPES.map((quotaType) => ({
    quotaType,
    planDefault: 0,
    limitOverride: null,
    effectiveLimit: 0,
    currentUsage: 0,
  }));

  return (
    <VStack spacing={4} align="stretch" data-testid="quota-tab">
      <Flex justify="flex-end" px={4} pt={4}>
        <Button
          size="sm"
          leftIcon={<RepeatIcon />}
          variant="outline"
          onClick={() => reconcileMutation.mutate()}
          isLoading={reconcileMutation.isPending}
          data-testid="reconcile-quotas-button"
        >
          {t('admin.quotas.reconcile')}
        </Button>
      </Flex>
      <Box overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th color={secondaryTextColor} borderColor={borderColor} fontSize={{ sm: '10px', lg: '12px' }} textTransform="uppercase" letterSpacing="wider">
                {t('admin.quotas.type')}
              </Th>
              <Th isNumeric color={secondaryTextColor} borderColor={borderColor} fontSize={{ sm: '10px', lg: '12px' }} textTransform="uppercase" letterSpacing="wider">
                {t('admin.quotas.planDefault')}
              </Th>
              <Th isNumeric color={secondaryTextColor} borderColor={borderColor} fontSize={{ sm: '10px', lg: '12px' }} textTransform="uppercase" letterSpacing="wider">
                {t('admin.quotas.override')}
              </Th>
              <Th color={secondaryTextColor} borderColor={borderColor} fontSize={{ sm: '10px', lg: '12px' }} textTransform="uppercase" letterSpacing="wider">
                {t('admin.quotas.currentUsage')}
              </Th>
              <Th borderColor={borderColor} />
            </Tr>
          </Thead>
          <Tbody>
            {displayQuotas.map((quota) => {
              const effectiveLimit = quota.effectiveLimit;
              const usagePercent = effectiveLimit > 0 ? (quota.currentUsage / effectiveLimit) * 100 : 0;
              const isEditing = editingType === quota.quotaType;

              return (
                <Tr key={quota.quotaType} data-testid={`quota-row-${quota.quotaType}`}>
                  <Td borderColor={borderColor} py="12px">
                    <Text color={textColor} fontSize="sm" fontWeight="500">
                      {t(QUOTA_TYPE_I18N[quota.quotaType])}
                    </Text>
                  </Td>
                  <Td isNumeric borderColor={borderColor} py="12px">
                    <Text color={textColor} fontSize="sm" fontWeight="500">
                      {quota.planDefault}
                    </Text>
                  </Td>
                  <Td isNumeric borderColor={borderColor} py="12px">
                    {isEditing ? (
                      <VStack spacing={2} align="stretch">
                        <Input
                          size="sm"
                          type="number"
                          min={0}
                          borderRadius="16px"
                          value={editLimit}
                          onChange={(e) => setEditLimit(e.target.value)}
                          placeholder={t('admin.quotas.limit')}
                        />
                        <Textarea
                          size="sm"
                          borderRadius="16px"
                          value={editReason}
                          onChange={(e) => setEditReason(e.target.value)}
                          placeholder={t('admin.quotas.reason')}
                          rows={2}
                        />
                        <Flex gap={2}>
                          <Button
                            size="xs"
                            variant="brand"
                            onClick={() => handleSave(quota.quotaType)}
                            isLoading={saveMutation.isPending}
                            data-testid={`quota-save-${quota.quotaType}`}
                          >
                            {t('admin.quotas.save')}
                          </Button>
                          <Button size="xs" variant="ghost" onClick={cancelEdit}>
                            {t('admin.quotas.cancel')}
                          </Button>
                        </Flex>
                      </VStack>
                    ) : (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        {quota.limitOverride !== null ? quota.limitOverride : '—'}
                      </Text>
                    )}
                  </Td>
                  <Td borderColor={borderColor} py="12px">
                    <Flex align="center" gap={2}>
                      <Progress
                        value={usagePercent}
                        size="sm"
                        flex={1}
                        colorScheme={usagePercent > 90 ? 'red' : usagePercent > 70 ? 'yellow' : 'green'}
                        borderRadius="full"
                      />
                      <Text color={secondaryTextColor} fontSize="xs" whiteSpace="nowrap">
                        {quota.currentUsage}/{effectiveLimit}
                      </Text>
                    </Flex>
                  </Td>
                  <Td borderColor={borderColor} py="12px">
                    {!isEditing && (
                      <Flex gap={1}>
                        <IconButton
                          aria-label={t('admin.quotas.setOverride')}
                          icon={<EditIcon />}
                          size="xs"
                          variant="ghost"
                          onClick={() => startEdit(quota)}
                          data-testid={`quota-edit-${quota.quotaType}`}
                        />
                        {quota.limitOverride !== null && (
                          <IconButton
                            aria-label={t('admin.quotas.removeOverride')}
                            icon={<DeleteIcon />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => removeMutation.mutate(quota.quotaType)}
                            data-testid={`quota-remove-${quota.quotaType}`}
                          />
                        )}
                      </Flex>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </VStack>
  );
}
